import ISensor from 'app/business-logic/domain-models/ISensor';
import { ApolloProvider } from 'core/net/ApolloProvider';
import Guid from 'core/types/Guid';

import { convertToSensor } from './Convert';
import { FETCH_GRAPH_SENSORS } from './graphql/facility.fetchGraphSensors';
import { FETCH_SENSORS } from './graphql/facility.fetchSensors';
import { FETCH_SENSORS_FOR_SENSOR_GROUP_QUERY } from './graphql/facility.fetchSensorsForSensorGroup';
import {
  FetchGraphSensorsQuery,
  FetchGraphSensorsQueryVariables,
  FetchSensorsForSensorGroupQuery,
  FetchSensorsForSensorGroupQueryVariables,
  FetchSensorsQuery,
  FetchSensorsQueryVariables,
  ProcessClass,
} from 'app/__generated__/global';

/**
 * Fetch all sensors contained in the given sensor groups.
 *
 * @param sensorGroupIds Sensor groups to filter on
 * @returns Sensors keyed by id
 */
export async function fetchSensorsForSensorGroups(sensorGroupIds: Guid[]): Promise<Map<Guid, ISensor>> {
  const sensorGroupFilter = {
    id_in: sensorGroupIds,
  };

  const result = new Map<Guid, ISensor>();

  const { data } = await ApolloProvider.global().query<
    FetchSensorsForSensorGroupQuery,
    FetchSensorsForSensorGroupQueryVariables
  >({
    query: FETCH_SENSORS_FOR_SENSOR_GROUP_QUERY,
    variables: { measurementGroupFilter: sensorGroupFilter },
  });

  const sensorGroups = data?.facility?.measurementGroups;
  if (!(sensorGroups && sensorGroups.length)) {
    return result;
  }

  sensorGroups.forEach(sensorGroup => {
    const sensorGroupId = sensorGroup.id;

    sensorGroup.processVariables.forEach(processVariable => {
      const sensor = convertToSensor.from(processVariable, sensorGroupId);
      if (sensor) {
        result.set(sensor.id, sensor);
      }
    });
  });

  return result;
}

/**
 * Fetches information about a set of sensors.
 *
 * @param sensorIds Identifies the sensors to retrieve the information for.
 * @returns Sensors keyed by id
 */
export async function fetchSensors(sensorIds: Guid[]): Promise<Map<Guid, ISensor>> {
  const sensorFilter = {
    id_in: sensorIds,
  };

  const result = new Map<Guid, ISensor>();

  const { data } = await ApolloProvider.global().query<FetchSensorsQuery, FetchSensorsQueryVariables>({
    query: FETCH_SENSORS,
    variables: { sensorFilter },
  });

  const processVariables = data?.facility?.processVariables;
  if (!(processVariables && processVariables.length)) {
    return result;
  }

  processVariables.forEach(processVariable => {
    const sensor = convertToSensor.from(processVariable);
    if (!sensor?.id) return;
    result.set(sensor.id, sensor);
  });

  return result;
}

export async function fetchGraphSensorsForMonitor(monitorId: Guid): Promise<ISensor[]> {
  const monitorFilter = {
    id_in: [monitorId],
    processClass_is: ProcessClass.Monitor,
  };

  const { data } = await ApolloProvider.global().query<FetchGraphSensorsQuery, FetchGraphSensorsQueryVariables>({
    query: FETCH_GRAPH_SENSORS,
    variables: { filter: monitorFilter },
  });

  const monitors = data?.facility?.processes;
  const [monitor] = monitors ?? [];

  if (!monitor) {
    throw new Error(`Could not find monitor: ${monitorId}`);
  }

  if (!('displayConfig' in monitor)) {
    throw new Error(`Monitor has no display config: ${monitorId}`);
  }

  const graphVariables = monitor.displayConfig?.graphVariables ?? [];

  return graphVariables
    .flatMap(({ processVariable }) => {
      if (!processVariable) return [];
      return convertToSensor.from(processVariable);
    })
    .filter(graphVariable => graphVariable !== null) as ISensor[];
}
